import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { BookOpen, Clock, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import LessonContent from '../components/Lesson/LessonContent';
import LessonNavigation from '../components/Lesson/LessonNavigation';
import CodeEditor from '../components/CodeEditor/CodeEditor';
import { courseService } from '../services/courseService';
import { codeExecutionService } from '../services/codeExecutionService';

const LessonView = () => {
  const { lessonId } = useParams();
  const [lesson, setLesson] = useState(null);
  const [nextLesson, setNextLesson] = useState(null);
  const [previousLesson, setPreviousLesson] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeExerciseIndex, setActiveExerciseIndex] = useState(0);
  
  useEffect(() => {
    fetchLesson();
  }, [lessonId]);
  
  const fetchLesson = async () => {
    try {
      setIsLoading(true);
      
      // Fetch current lesson
      const lessonData = await courseService.getLessonById(lessonId);
      setLesson(lessonData);
      
      // Fetch next and previous lessons
      try {
        const nextData = await courseService.getNextLesson(lessonId);
        setNextLesson(nextData);
      } catch (error) {
        setNextLesson(null);
      }
      
      try {
        const prevData = await courseService.getPreviousLesson(lessonId);
        setPreviousLesson(prevData);
      } catch (error) {
        setPreviousLesson(null);
      }
    } catch (error) {
      toast.error('Failed to load lesson');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRunCode = async (code) => {
    try {
      const result = await codeExecutionService.executeCode(code);
      return result;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  };
  
  const handleComplete = () => {
    // TODO: Implement progress tracking
    toast.success('Lesson marked as complete!');
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading lesson...</p>
        </div>
      </div>
    );
  }
  
  if (!lesson) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Lesson Not Found</h1>
          <Link to="/courses" className="btn-primary">
            Browse Courses
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link
            to={`/courses/${lesson.course_id}`}
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Course
          </Link>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{lesson.title}</h1>
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center">
              <BookOpen size={16} className="mr-1" />
              {lesson.content_type}
            </div>
            <div className="flex items-center">
              <Clock size={16} className="mr-1" />
              {lesson.duration_minutes} minutes
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Lesson Content */}
          <div className="bg-white rounded-lg shadow-sm p-8">
            <LessonContent content={lesson.content} />
            
            {/* Learning Objectives */}
            {lesson.learning_objectives && lesson.learning_objectives.length > 0 && (
              <div className="mt-8 p-4 bg-primary-50 rounded-lg">
                <h3 className="font-semibold text-primary-900 mb-2">
                  Learning Objectives
                </h3>
                <ul className="list-disc list-inside space-y-1 text-primary-800">
                  {lesson.learning_objectives.map((objective, index) => (
                    <li key={index}>{objective}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          {/* Right: Code Editor / Exercises */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {lesson.exercises && lesson.exercises.length > 0 ? (
              <>
                {/* Exercise Tabs */}
                {lesson.exercises.length > 1 && (
                  <div className="flex border-b border-gray-200">
                    {lesson.exercises.map((exercise, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveExerciseIndex(index)}
                        className={`px-4 py-2 text-sm font-medium ${
                          activeExerciseIndex === index
                            ? 'text-primary-600 border-b-2 border-primary-600'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Exercise {index + 1}
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Exercise Content */}
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-xl font-semibold mb-2">
                    {lesson.exercises[activeExerciseIndex].title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {lesson.exercises[activeExerciseIndex].instruction}
                  </p>
                  
                  {/* Hints */}
                  {lesson.exercises[activeExerciseIndex].hints && (
                    <details className="mb-4">
                      <summary className="cursor-pointer text-primary-600 font-medium">
                        💡 Show Hints
                      </summary>
                      <ul className="mt-2 list-disc list-inside space-y-1 text-gray-600">
                        {lesson.exercises[activeExerciseIndex].hints.map((hint, i) => (
                          <li key={i}>{hint}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
                
                {/* Code Editor */}
                <div className="h-[600px]">
                  <CodeEditor
                    initialCode={lesson.exercises[activeExerciseIndex].starter_code}
                    onRun={handleRunCode}
                  />
                </div>
              </>
            ) : (
              <div className="h-[700px]">
                <CodeEditor
                  initialCode="# Python playground\n# Try out what you've learned!\n\n"
                  onRun={handleRunCode}
                />
              </div>
            )}
          </div>
        </div>
        
        {/* Navigation */}
        <div className="mt-8">
          <LessonNavigation
            previousLesson={previousLesson}
            nextLesson={nextLesson}
            onComplete={handleComplete}
          />
        </div>
      </div>
    </div>
  );
};

export default LessonView;

